import { saveFile } from '@modules/file-menagement/fileUtils';
import { AssetsRequests } from 'src/requests/assets';
import { PriorityQueue } from 'src/utils/priorityQueue';
import { createStore } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { DownloadFileState, DownloadParam } from './interfaces';

export const downloadFileFactory = () => {
  const queue = new PriorityQueue<DownloadParam>({ batchSize: 5 });

  return createStore(
    immer<DownloadFileState>((set, get) => {
      const handleDownloadPrivateFile = async (
        fileId: string,
      ): Promise<Blob | undefined> => {
        const state = get();
        if (state.contentMap[fileId]) {
          return state.contentMap[fileId];
        }

        if (state.loadingMap[fileId]) {
          return;
        }

        set((s) => {
          s.loadingMap[fileId] = true;
          s.progressMap[fileId] = 0;
          delete s.queueMap[fileId];
        });

        try {
          const blob = await AssetsRequests.getPrivateFile(
            fileId,
            ({ loaded, total }) => {
              const pct = total ? (loaded / total) * 100 : 0;
              set((s) => {
                s.progressMap[fileId] = pct;
              });
            },
          );

          set((s) => {
            s.contentMap[fileId] = blob;
            delete s.loadingMap[fileId];
            delete s.progressMap[fileId];
          });

          return blob;
        } catch (error) {
          console.error('download error', error);
        }
      };

      let running = false;
      const runQueue = async () => {
        if (running) return;
        const item = queue.getNextBatch();
        if (!item.length) {
          running = false;
          return;
        }

        running = true;

        await Promise.all(
          item.map((item) => handleDownloadPrivateFile(item.data.fileId)),
        );

        running = false;
        runQueue();
      };

      return {
        loadingMap: {},
        progressMap: {},
        queueMap: {},
        contentMap: {},

        queueDownload: (data, priority = 0) => {
          const state = get();
          if (
            state.contentMap[data.fileId] ||
            state.queueMap[data.fileId] ||
            state.loadingMap[data.fileId]
          ) {
            return;
          }

          set((s) => {
            s.queueMap[data.fileId] = true;
          });

          queue.enqueue({
            data,
            id: data.fileId,
            priority,
          });

          runQueue();
        },

        dropFromQueue: (fileId) => {
          set((s) => {
            s.queueMap[fileId] = false;
          });
          queue.drop(fileId);
        },

        //! this should be removed soon. we don't need to support url or blob at this state.
        preDownloadContent: async ({ blob, fileUrl, fileName, fileId }) => {
          const id = fileId ?? fileName;

          if (get().contentMap[id]) {
            return URL.createObjectURL(get().contentMap[id]);
          }

          if (get().loadingMap[id]) {
            return;
          }

          set((s) => {
            s.loadingMap[id] = true;
            s.progressMap[id] = 0;
          });

          try {
            let fileBlob: Blob | undefined;

            if (blob) {
              fileBlob = blob;
            } else if (fileUrl) {
              const resp = await fetch(fileUrl);
              const reader = resp.body!.getReader();
              const total = Number(resp.headers.get('Content-Length') ?? 0);
              let loaded = 0;
              const chunks: Uint8Array[] = [];

              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                chunks.push(value!);
                loaded += value!.length;
                const pct = total ? (loaded / total) * 100 : 0;
                set((s) => {
                  s.progressMap[id] = pct;
                });
              }

              fileBlob = new Blob(chunks);
            } else if (fileId) {
              fileBlob = await AssetsRequests.getPrivateFile(
                fileId,
                ({ loaded, total }) => {
                  const pct = total ? (loaded / total) * 100 : 0;
                  set((s) => {
                    s.progressMap[id] = pct;
                  });
                },
              );
            } else {
              throw new Error(
                'downloadContent: é necessário blob, fileUrl ou fileId',
              );
            }

            set((s) => {
              s.contentMap[id] = fileBlob;
            });

            return URL.createObjectURL(fileBlob);
          } catch (error) {
            console.error('download error', error);
          } finally {
            set((s) => {
              s.loadingMap[id] = false;
              s.progressMap[id] = 0;
            });
          }
        },

        //! this should be changed soon. it should call handleDownloadPrivateFile and return the blob. Caller is in charge of saving the file.
        downloadContent: async ({ blob, fileUrl, fileName, fileId }) => {
          const fileBlobUrl = await get().preDownloadContent({
            blob,
            fileUrl,
            fileName,
            fileId,
          });
          if (fileBlobUrl) {
            saveFile(fileName, fileBlobUrl);
          }
        },

        resetFor: (fileId) => {
          set((s) => {
            s.loadingMap[fileId] = false;
            s.progressMap[fileId] = 0;
          });
        },
      };
    }),
  );
};
